import { useQuery } from '@tanstack/react-query';
import { Webhook, RefreshCw } from 'lucide-react';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { CardGrid } from '@reflex/ui/components/shared/CardGrid';
import { Button } from '@reflex/ui/components/ui/button';
import { AdminSubPageLayout } from '@reflex/ui/components/layout/AdminSubPageLayout';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { StatCard } from '@reflex/ui/components/shared/StatCard';
import { SectionLabel, PageDescription } from '@reflex/ui/components/layout/Typography';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { timeAgo, formatDateTimeWithSeconds } from '@reflex/ui/lib/format';
import { WebhookSourceCard } from './webhooks/WebhookSourceCard';
import { WebhookWiringSection } from './webhooks/WebhookWiringSection';

export function AdminWebhooksPage() {
  const {
    data: activity,
    isLoading,
    isError,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: queryKeys.webhookActivity,
    queryFn: api.getWebhookActivity,
    refetchInterval: 15_000,
  });

  const sources = activity?.sources ?? [];
  const totalReceived = sources.reduce((sum, s) => sum + s.totalReceived, 0);
  const totalErrors = sources.reduce((sum, s) => sum + s.totalErrors, 0);
  const activeSources = sources.filter((s) => s.lastReceivedAt !== null).length;

  return (
    <AdminSubPageLayout
      title="Webhooks"
      description="Monitor incoming webhook activity and registered providers/receivers"
      actions={
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? <Spinner /> : <RefreshCw className="size-3.5" />}
          Refresh
        </Button>
      }
    >
      <SectionLabel className="mb-3">Activity</SectionLabel>
      <CardGrid cols={4}>
        <StatCard align="center" label="Sources" value={sources.length} />
        <StatCard align="center" label="Active" value={activeSources} />
        <StatCard align="center" label="Total received" value={totalReceived} />
        <StatCard
          align="center"
          label="Total errors"
          value={totalErrors}
          valueClassName={totalErrors > 0 ? 'text-status-error' : ''}
        />
      </CardGrid>

      {activity?.serverStartedAt && (
        <PageDescription className="text-xs mb-4">
          Showing retained inbox activity. Server started {timeAgo(activity.serverStartedAt)}
          {dataUpdatedAt > 0 && ` · Updated ${formatDateTimeWithSeconds(dataUpdatedAt)}`}
        </PageDescription>
      )}

      <AsyncContent
        isLoading={isLoading}
        isError={isError}
        loadingMessage="Loading webhook activity…"
        errorMessage="Failed to load webhook activity."
      >
        {sources.length === 0 ? (
          <EmptyState
            icon={<Webhook className="h-5 w-5 text-muted-foreground" />}
            title="No webhook sources"
            description="Webhook activity will appear here when integrations send events"
          />
        ) : (
          <div className="space-y-3">
            {sources.map((source) => (
              <WebhookSourceCard key={source.source} source={source} />
            ))}
          </div>
        )}
      </AsyncContent>

      <div className="mt-6">
        <WebhookWiringSection />
      </div>
    </AdminSubPageLayout>
  );
}
